import { FC } from 'react';
import { useIntl } from 'react-intl';

import {
  CohortKpis,
  EMPTY_VALUE,
  KPI_RETENTION_OFFSETS,
  RetentionMode,
  formatEuro,
  formatInteger,
  formatPercent,
} from '../../models/SubscriberCohortsModel';

const TILE_MIN_WIDTH_PX = 150;

type Tile = {
  key: string;
  label: string;
  value: string;
  hint?: string;
};

type Props = {
  kpis: CohortKpis;
  mode: RetentionMode;
};

// Headline numbers of the range. Retention and LTV are the weighted averages
// over the cohorts that have completed that month; until one has, "—".
const CohortKpiTiles: FC<Props> = ({ kpis, mode }) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `subscriberCohorts.${id}` }, values);

  const tiles: Tile[] = [
    {
      key: 'subscribers',
      label: t('kpiSubscribers'),
      value: formatInteger(kpis.subscribers),
      hint: t('kpiTrialShare', { share: formatPercent(kpis.trial_share) }),
    },
    {
      key: 'activeNow',
      label: t('kpiActiveNow'),
      value: formatPercent(kpis.active_now_share),
      hint: t('kpiActiveNowCount', { count: formatInteger(kpis.active_now) }),
    },
    ...KPI_RETENTION_OFFSETS.map((offset) => ({
      key: offset,
      label: t('kpiRetention', { month: offset.toUpperCase() }),
      value: formatPercent(kpis.retention[offset][mode]),
    })),
    {
      key: 'ltv6',
      label: t('kpiLtv', { months: 6 }),
      value: formatEuro(kpis.ltv.first_6_months, true),
      hint: t('kpiLtvHint', { last: 5 }),
    },
    {
      key: 'ltv12',
      label: t('kpiLtv', { months: 12 }),
      value: formatEuro(kpis.ltv.first_12_months, true),
      hint: t('kpiLtvHint', { last: 11 }),
    },
  ];

  return (
    <div className="d-flex flex-wrap gap-4 mb-8">
      {tiles.map((tile) => (
        <div
          key={tile.key}
          className="border rounded px-5 py-4 flex-grow-1"
          style={{ minWidth: `${TILE_MIN_WIDTH_PX}px` }}
          title={tile.value === EMPTY_VALUE ? t('kpiNotAvailable') : undefined}
        >
          <div className="text-muted fs-7">{tile.label}</div>
          <div className="fs-2 fw-bolder text-dark">{tile.value}</div>
          {tile.hint && <div className="text-muted fs-8">{tile.hint}</div>}
        </div>
      ))}
    </div>
  );
};

export default CohortKpiTiles;
